import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { loadConfig, saveConfig, loadPresets, savePresets } from '@/lib/timer/storage';
import { DEFAULT_CONFIG } from '@/lib/timer/constants';
import { useTimer } from '@/lib/timer/useTimer';
import TimerSetup from '@/components/timer/TimerSetup';
import TimerRun from '@/components/timer/TimerRun';

export default function Timer() {
    const { user } = useAuth();
    const userId = user?.id ?? null;

    const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
    const [presets, setPresets] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [mode, setMode] = useState('setup');

    const timer = useTimer();

    useEffect(() => {
        if (loaded || user === null) return;
        setConfig(loadConfig(userId));
        setPresets(loadPresets(userId));
        setLoaded(true);
    }, [user, userId, loaded]);

    useEffect(() => {
        if (!loaded) return;
        saveConfig(config, userId);
    }, [config, userId, loaded]);

    const handleChange = useCallback((nextConfig) => {
        setConfig(nextConfig);
    }, []);

    const handleStart = useCallback(async () => {
        await timer.start(config);
        setMode('run');
    }, [timer, config]);

    const handleBack = useCallback(() => {
        setMode('setup');
    }, []);

    const handleSavePreset = useCallback((name, presetConfig) => {
        const next = [...presets, { name, config: presetConfig }];
        setPresets(next);
        savePresets(next, userId);
    }, [presets, userId]);

    const handleLoadPreset = useCallback((preset) => {
        setConfig({ ...preset.config });
    }, []);

    const handleDeletePreset = useCallback((idx) => {
        const next = presets.filter((_, i) => i !== idx);
        setPresets(next);
        savePresets(next, userId);
    }, [presets, userId]);

    if (!loaded) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
        );
    }

    if (mode === 'run') {
        return <TimerRun timer={timer} config={config} onBack={handleBack} />;
    }

    return (
        <TimerSetup
            config={config}
            onChange={handleChange}
            onStart={handleStart}
            presets={presets}
            onSavePreset={handleSavePreset}
            onLoadPreset={handleLoadPreset}
            onDeletePreset={handleDeletePreset}
        />
    );
}
